import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { messageAPI } from '../../api';

const LoginBoxContainer = styled.div`
  background-color: white;
  border: 1px solid #eaeaea;
  border-radius: 0.5rem;
  overflow: hidden;
`;

const LoginHeader = styled.div`
  background-color: #ef4444;
  color: white;
  padding: 0.75rem 1rem;
  font-weight: 500;
`;

const LoginForm = styled.form<{ loading: boolean }>`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  ${({ loading }) => loading && `
    filter: grayscale(100%) blur(2px);
    pointer-events: none;
  `}
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Input = styled.input`
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  width: 100%;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const LoginButton = styled.button`
  flex: 1;
  background-color: #ef4444;
  color: white;
  border-radius: 0.25rem;
  padding: 0.5rem 0;
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  
  &:hover {
    background-color: #dc2626;
  }
`;

const RegisterButton = styled(Link)`
  flex: 1;
  background-color: white;
  border: 1px solid #ef4444;
  color: #ef4444;
  border-radius: 0.25rem;
  padding: 0.5rem 0;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
  cursor: pointer;
  
  &:hover {
    background-color: #fee2e2;
  }
`;

const UserInfoBox = styled.div`
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: white;
`;

const UserHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
`;

const UserAvatar = styled.div`
  width: 3rem;
  height: 3rem;
  background-color: #ef4444;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: bold;
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const UserName = styled.div`
  font-weight: 600;
  color: #111827;
  font-size: 1.125rem;
`;

const UserLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  justify-content: space-around;
  margin-top: 0.5rem;
  background-color: white;
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid #eaeaea;
`;

const UserLink = styled(Link)`
  font-size: 0.875rem;
  color: #4b5563;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.2s;
  
  &:hover {
    color: #ef4444;
    text-decoration: underline;
  }
  
  &:before {
    content: '•';
    color: #ef4444;
  }
`;

const BadgeIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #ef4444;
  color: white;
  border-radius: 9999px;
  padding: 0.125rem 0.375rem;
  font-size: 0.5rem;
  font-weight: bold;
  margin-left: 0.1rem;
`;

const LogoutButton = styled.button`
  background-color: #ef4444;
  border: none;
  color: white;
  border-radius: 0.375rem;
  padding: 0.625rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 0.5rem;
  transition: all 0.2s;
  
  &:hover {
    background-color: #dc2626;
    transform: translateY(-2px);
  }
`;

const LoginBox: React.FC = () => {
  const [loginId, setLoginId] = useState('');
  const [loginPw, setLoginPw] = useState('');
  const [unreadMessages, setUnreadMessages] = useState(0);

  const { isAuthenticated, user, loading, login, logout } = useAuth();

  // 안 읽은 쪽지 수 조회
  const fetchUnreadMessagesCount = async () => {
    if (isAuthenticated && user) {
      try {
        const response = await messageAPI.getUnreadCount();
        if (response.data.success) {
          setUnreadMessages(response.data.unreadCount);
        }
      } catch (error) {
        console.error('안 읽은 쪽지 수 조회 오류:', error);
      }
    }
  };

  // 인증 상태 변경 시 안 읽은 쪽지 수 조회
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUnreadMessagesCount();
    }
  }, [isAuthenticated, user]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(loginId, loginPw);
      setLoginId('');
      setLoginPw('');
    } catch (error) {
      console.error('로그인 오류:', error);
      alert('로그인에 실패했습니다. 아이디와 비밀번호를 확인해주세요.');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('로그아웃 오류:', error);
    }
  };

  return (
    <LoginBoxContainer>
      <LoginHeader>
        {isAuthenticated ? '내 정보' : '로그인'}
      </LoginHeader>

      {isAuthenticated && user ? (
        <UserInfoBox>
          <UserHeader>
            <UserAvatar>
              {user.username.charAt(0).toUpperCase()}
            </UserAvatar>
            <div>
              <UserName>{user.username}님</UserName>
              <UserLink to="/mypage/points">포인트: {user.points?.toLocaleString() || 0}P</UserLink>
            </div>

          </UserHeader>
          <UserLinks>
            <UserLink to="/mypage/info">내 정보</UserLink>
            <UserLink to="/mypage/messages">
              쪽지
              {unreadMessages > 0 && <BadgeIcon>{unreadMessages}</BadgeIcon>}
            </UserLink>
          </UserLinks>
          <LogoutButton onClick={handleLogout}>로그아웃</LogoutButton>
        </UserInfoBox>
      ) : (
        <LoginForm onSubmit={handleLogin} loading={loading}>
          <InputGroup>
            <Input
              type="text"
              placeholder="아이디"
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
              required
            />
          </InputGroup>
          <InputGroup>
            <Input
              type="password"
              placeholder="비밀번호"
              value={loginPw}
              onChange={(e) => setLoginPw(e.target.value)}
              required
            />
          </InputGroup>
          <ButtonGroup>
            <LoginButton type="submit">로그인</LoginButton>
            <RegisterButton to="/register">회원가입</RegisterButton>
          </ButtonGroup>
        </LoginForm>
      )}
    </LoginBoxContainer>
  );
};

export default LoginBox; 